/**
 * SEOHead — deterministic per-page meta sync
 * ==========================================
 * react-helmet-async (v2) reconciles all `data-rh` meta tags on hydration and
 * (in this app's multi-Helmet setup) ends up STRIPPING the correct per-page
 * description / og / twitter tags that the prerenderer injected — leaving the
 * JS-rendered DOM with no description. Social scrapers & Ahrefs read the raw
 * HTML (already correct), but JS-rendering crawlers (Google WRS) see the
 * stripped DOM.
 *
 * Rather than fight helmet's reconcile ordering, this component works
 * imperatively: it snapshots the prerendered values at script load (before
 * hydration strips them) and, after helmet settles, writes them back WITHOUT a
 * `data-rh` attribute so helmet leaves them alone permanently. CMS values from
 * useSEO take precedence when present.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';

const getMeta = (sel) => {
  if (typeof document === 'undefined') return '';
  const el = document.querySelector(sel);
  return el ? el.getAttribute('content') || '' : '';
};

// Snapshot the prerendered per-page tags once, at first script execution.
const SNAP = {
  description: getMeta('meta[name="description"]'),
  ogDescription: getMeta('meta[property="og:description"]'),
  ogTitle: getMeta('meta[property="og:title"]'),
  ogImage: getMeta('meta[property="og:image"]'),
  twitterImage: getMeta('meta[name="twitter:image"]'),
};

const setMeta = (attr, key, value) => {
  if (!value || typeof document === 'undefined') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]:not([data-rh])`)
    || document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
  el.removeAttribute('data-rh'); // take it out of helmet's control so it persists
};

const setCanonical = (href) => {
  if (!href || typeof document === 'undefined') return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  el.removeAttribute('data-rh');
};

export default function SEOHead({ defaults = {} }) {
  const seo = useSEO(defaults);
  const location = useLocation();

  useEffect(() => {
    const description = seo.description || SNAP.description;
    const ogDescription = seo.ogDescription || description || SNAP.ogDescription;
    const ogTitle = seo.ogTitle || SNAP.ogTitle;
    const ogImage = seo.ogImage || SNAP.ogImage;
    const twitterImage = seo.ogImage || SNAP.twitterImage || SNAP.ogImage;
    // Self-referencing canonical (CMS value wins) — strip any query/hash.
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://corvana.com.au';
    const canonical = seo.canonical || `${origin}${location.pathname}`;

    // Assert twice: once after helmet's initial reconcile, once as a safety net.
    const apply = () => {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', ogDescription);
      setMeta('property', 'og:title', ogTitle);
      setMeta('property', 'og:image', ogImage);
      setMeta('name', 'twitter:description', ogDescription);
      setMeta('name', 'twitter:image', twitterImage);
      setCanonical(canonical);
    };
    const t1 = setTimeout(apply, 60);
    const t2 = setTimeout(apply, 400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [seo, location.pathname]);

  return null;
}
